/**
 * Text constants for UI
 */
export const TEXTS = {
  CAMERA_ACCESS: "Camera Access",
  ALLOW_CAMERA:
    "Please allow the app to access the camera. It is required for the core functionality of the app.",
  ENABLE_CAMERA: "Enable Camera",

  LOCATION_ACCESS: "Enable Location",
  ALLOW_LOCATION:
    "Please allow the app to record geolocation of the scans. We use this data to find potential sources of counterfeit products.",
  ENABLE_LOCATION: "Enable Location",

  SCANNING_HEADER: "How to scan?",
  START_SCANNING: "Start Scanning",

  SCAN_QR: "Bring the QR on the product into focus",
  FOCUS_QR: "Focus on QR code and click\nthe camera button",
  VERIFYING: "Authentication in Progress",
  INVALID_QR: "Invalid QR code detected",
  QR_DETECTED: "Product Identified. Hold Still...",
  MOVE_CLOSER: "Move closer to the product",
  MOVE_AWAY: "Move slightly away from the product",

  GENUINE: "Genuine",
  GENUINE_DESCRIPTION: "This product is genuine",
  COUNTERFEIT: "Counterfeit",
  COUNTERFEIT_DESCRIPTION: "This product appears to be counterfeit",
  CANNOT_CONFIRM: "Cannot Confirm",
  CANNOT_CONFIRM_DESCRIPTION: "Cannot confirm authenticity",

  SCAN_AGAIN: "Scan Again",
  KNOW_MORE: "Know More",
  CONTACT_SUPPORT: "Contact Support",
};

/**
 * Flow types for the scan experience
 */
export const FLOW = {
  IMAGE_STREAM: "IMAGE_STREAM",
  VIDEO_STREAM: "VIDEO_STREAM",
};

/**
 * Default result configurations
 */
export const DEFAULT_RESULT_CONFIG: Record<string, any> = {
  GENUINE: {
    icon: "https://vyu-web-sdk.enncrypto.com/v1_sdk_assets/Icons/genuine.png",
    text: "This product is genuine",
    status: "Genuine",
    scanAgainButton: "Scan Again",
    button1: "Know More",
    statusColour: "rgba(65, 173, 73, 1)",
    textBgColour: "rgba(65, 172, 72, 0.1)",
    bgColour: "rgba(247, 255, 248, 1)",
  },
  POTENTIAL_FAKE: {
    icon: "https://vyu-web-sdk.enncrypto.com/v1_sdk_assets/Icons/potential_fake.png",
    text: "Please contact us on WhatsApp with a clear image of the product",
    status: "Cannot Confirm",
    scanAgainButton: "Scan Again",
    button1: "Contact on Whatsapp",
    button2: "Know More",
    bgColour: "rgba(255, 239, 239, 1)",
    statusColour: "rgba(255, 0, 0, 1)",
    textBgColour: "rgba(255, 214, 214, 1)",
  },
  CANNOT_CONFIRM: {
    icon: "https://vyu-web-sdk.enncrypto.com/v1_sdk_assets/Icons/cannot_confirm.png",
    text: "Please contact us on WhatsApp",
    status: "Cannot Confirm",
    scanAgainButton: "Scan Again",
    button1: "Contact Support",
    bgColour: "rgba(255, 244, 213, 1)",
    statusColour: "rgba(170, 126, 0, 1)",
    textBgColour: "rgba(255, 234, 175, 1)",
  },
  ERROR: {
    icon: "https://vyu-web-sdk.enncrypto.com/v1_sdk_assets/Icons/potential_fake.png",
    text: "Something went wrong. Please try again later",
    status: "Cannot Confirm",
    scanAgainButton: "Scan Again",
    button1: "Contact on Whatsapp",
    button2: "Know More",
    bgColour: "rgba(255, 239, 239, 1)",
    statusColour: "rgba(255, 0, 0, 1)",
    textBgColour: "rgba(255, 214, 214, 1)",
  },
  INVALID_QR: {
    icon: "https://vyu-web-sdk.enncrypto.com/v1_sdk_assets/Icons/potential_fake.png",
    status: "Wrong QR Scanned",
    text: "Please check the QR and scan again",
    button1: "Know More",
    bgColour: "rgba(255, 239, 239, 1)",
    statusColour: "rgba(255, 0, 0, 1)",
    textBgColour: "rgba(255, 214, 214, 1)",
  },
  REGION_EXCLUDED_GENUINE: {
    icon: "https://vyu-web-sdk.enncrypto.com/v1_sdk_assets/Icons/cannot_confirm.png",
    text: "This product is not for sale in India",
    bgColour: "rgba(255, 244, 213, 1)",
    statusColour: "rgba(170, 126, 0, 1)",
    textBgColour: "rgba(255, 234, 175, 1)",
  },
  REGION_EXCLUDED_NON_GENUINE: {
    text: "This product is not for sale in India",
  },
};

/**
 * Backend camera indicators for detecting the back camera
 */
export const BACK_INDICATORS: string[] = [
  // English (universal baseline)
  'back', 'rear', 'environment', 'main',

  // Chinese (Simplified) - Already present, unchanged
  '后置', '后面', '后置相机 ',

  // Italian (Italy) - Expanding from 'posteriore'
  'posteriore', 'retro', 'dietro',

  // German (Deutsch) - Already present, unchanged
  'rück', 'hinten',

  // Spanish (Argentina) - Expanding from 'trasera', 'posterior'
  'trasera', 'posterior', 'atrás', 'trasero',

  // Hindi (India) - Already present, unchanged
  'पीछे', 'बैक', // Pīche

  // Polish (Polski) - Already present, unchanged
  'tył', 'tylny',

  // Portuguese (Brazil) - Expanding from 'traseira', 'posterior'
  'traseira', 'posterior', 'trás', 'de trás',

  // Thai (Thailand) - Expanding from 'ด้านหลัง'
  'ด้านหลัง', 'กล้องด้านหลัง', // Dān h̄lạng
  'หลัง', // H̄lạng (shortened form)
  '背面',
]

/**
 * Indicators to exclude from back camera detection
 */
export const EXCLUDE_INDICATORS: string[] = [
  // English (universal baseline)
  'wide', 'ultra', 'tele', 'dual', 'triple', 'super', 'macro', 'front', 'depth', 'penta', 'quad', 'peri', 'zoom', 'portrait', 'mono', 'pro', 'plus', 'lite',

  // Chinese (Simplified) - Updated with matching terms
  '广角', '超广角', '远摄', '双', '三重', '超级', '微距', '前置', '深度', '五摄', '四摄', '潜望', '变焦', '人像', '单色', '专业', '增强版', '轻量版',
  '长焦', '双镜头', '超广角相机', '双广角镜头', '三镜头',

  // Italian (Italy)
  'grandangolo', 'ultra', 'teleobiettivo', 'doppio', 'triplo', 'super', 'macro', 'frontale', 'profondità', 'penta', 'quadruplo', 'peri', 'zoom', 'ritratto', 'mono', 'pro', 'plus', 'lite',

  // German (Deutsch)
  'weitwinkel', 'ultra', 'tele', 'dual', 'dreifach', 'super', 'makro', 'front', 'tiefe', 'penta', 'quad', 'peri', 'zoom', 'porträt', 'mono', 'pro', 'plus', 'lite',

  // Spanish (Argentina)
  'gran angular', 'ultra', 'teleobjetivo', 'doble', 'triple', 'súper', 'macro', 'frontal', 'profundidad', 'penta', 'cuádruple', 'peri', 'zoom', 'retrato', 'mono', 'pro', 'plus', 'lite',

  // Hindi (India)
  'वाइड', 'अल्ट्रा', 'टेली', 'डुअल', 'ट्रिपल', 'सुपर', 'मैक्रो', 'फ्रंट', 'गहराई', 'पेंटा', 'क्वाड', 'पेरि', 'ज़ूम', 'पोर्ट्रेट', 'मोनो', 'प्रो', 'प्लस', 'लाइट',

  // Polish (Polski) - Updated with provided terms
  'szerokokątny', 'ultraszerokokątny', 'długoogniskowy', 'podwójny', 'trójobiektywowy', 'super', 'makro', 'przedni', 'głębia', 'penta', 'dwuobiektywowy', 'peri', 'zoom', 'portret', 'mono', 'pro', 'plus', 'lite',

  // Portuguese (Brazil)
  'grande angular', 'ultra', 'telefoto', 'duplo', 'triplo', 'super', 'macro', 'frontal', 'profundidade', 'penta', 'quádruplo', 'peri', 'zoom', 'retrato', 'mono', 'pro', 'plus', 'lite',

  // Thai (Thailand)
  'มุมกว้าง', 'อัลตร้า', 'เทเล', 'คู่', 'สามเท่า', 'ซุปเปอร์', 'มาโคร', 'ด้านหน้า', 'ความลึก', 'ห้า', 'สี่', 'ปริ', 'ซูม', 'ภาพบุคคล', 'โมโน', 'โปร', 'พลัส', 'ไลท์'
];
